import React, { useState } from 'react';
import { Shield, Lock, Download, Settings, Plus, AlertTriangle } from 'lucide-react';
import { WalletConnect } from './components/WalletConnect';
import { LockForm } from './components/LockForm';
import { LocksTable } from './components/LocksTable';
import { AllLocksTable } from './components/AllLocksTable';

const App: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'lock' | 'locks' | 'allLocks'>('lock');

    const tabs = [
        {
            id: 'lock' as const,
            label: 'New Lock',
            icon: Plus,
            description: 'Create new token lock'
        },
        {
            id: 'locks' as const,
            label: 'Your Locks',
            icon: Shield,
            description: 'Manage existing locks'
        },
        {
            id: 'allLocks' as const,
            label: 'All Locks',
            icon: Lock,
            description: 'Browse all token locks'
        }
    ];

    return (
        <div className="min-h-screen bg-black text-white">
            {/* Background Effects */}
            <div className="fixed inset-0 bg-gradient-to-b from-black via-zinc-900 to-black"></div>
            <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:20px_20px]"></div>

            {/* Header */}
            <header className="relative z-10 border-b border-zinc-800 bg-black/80 backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center space-x-4">
                            <div className="text-2xl font-bold text-white r">
                                <img
                                    src="/assets/img/kitsu_white.svg"
                                    alt="Kitsu Logo"
                                    className="h-16 w-auto"
                                />
                            </div>
                            <div className="hidden md:block text-sm text-zinc-400">
                                九尾の狐 | Nine-Tailed Prophecy
                            </div>
                        </div>
                        <WalletConnect />
                    </div>
                </div>
            </header>



            {/* Main Content */}
            <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <img
                        src="/assets/img/kitsu_white.svg"
                        alt="KITSU Logo"
                        className="mx-auto mb-6 glow w-1/4"
                    />
                    <h2 className="font-zenjirou text-white mb-4 ">
                        TokenLocker
                    </h2>
                    <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-6">
                        Secure your tokens with mystical time-based locks.
                        Lock, unlock, and manage your digital assets with the power of the nine-tailed fox.
                    </p>
                </div>

                {/* Tab Navigation */}
                <div className="flex justify-center mb-8">
                    <div className="bg-zinc-800/50 backdrop-blur-md rounded-xl border border-zinc-600 p-1 flex items-center gap-1">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-6 py-3 rounded-lg border transition-colors duration-200 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0 ${activeTab === tab.id
                                        ? 'bg-purple-400/20 text-purple-400 border-purple-400/30'
                                        : 'text-zinc-400 hover:text-zinc-300 hover:bg-zinc-700/30 border-transparent'
                                        }`}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span className="font-medium">{tab.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="space-y-8">
                    {activeTab === 'lock' && (
                        <div>
                            <LockForm onSuccess={() => setActiveTab('locks')} />
                            <div className="bg-yellow-900/20 border border-yellow-800/30 rounded-lg p-4 max-w-2xl mx-auto mt-4">
                                <div className="flex items-center justify-center gap-2 text-center">
                                    {/* <AlertTriangle className="w-8 h-8 text-yellow-400 mr-2" /> */}
                                    <p className="text-yellow-400 text-sm font-light">
                                        Smart Contracts for Kitsu Locker have not been audited yet, but they have been extensively tested and are currently operating as expected.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'locks' && (
                        <div>
                            <LocksTable onRefresh={() => { }} />
                        </div>
                    )}

                    {activeTab === 'allLocks' && (
                        <div>
                            <AllLocksTable onRefresh={() => { }} />
                        </div>
                    )}
                </div>



                {/* Features Section */}
                <div className="mt-20 grid md:grid-cols-3 gap-8">
                    <div className="bg-zinc-800/50 backdrop-blur-md rounded-xl border border-zinc-600 p-6 text-center">
                        <div className="w-12 h-12 bg-purple-400/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                            <Lock className="w-6 h-6 text-purple-400" />
                        </div>
                        <h3 className="text-xl font-zenjirou text-white mb-2">Time-Based Locks</h3>
                        <p className="text-zinc-400 text-sm">
                            Lock your tokens for a specified period with automatic unlock functionality
                        </p>
                    </div>

                    <div className="bg-zinc-800/50 backdrop-blur-md rounded-xl border border-zinc-600 p-6 text-center">
                        <div className="w-12 h-12 bg-green-400/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                            <Download className="w-6 h-6 text-green-400" />
                        </div>
                        <h3 className="text-xl font-zenjirou text-white mb-2">Secure Withdrawal</h3>
                        <p className="text-zinc-400 text-sm">
                            Withdraw your tokens once the lock period has expired
                        </p>
                    </div>

                    <div className="bg-zinc-800/50 backdrop-blur-md rounded-xl border border-zinc-600 p-6 text-center">
                        <div className="w-12 h-12 bg-blue-400/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                            <Settings className="w-6 h-6 text-blue-400" />
                        </div>
                        <h3 className="text-xl font-zenjirou text-white mb-2">Transfer Ownership</h3>
                        <p className="text-zinc-400 text-sm">
                            Transfer lock ownership to other addresses when needed
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <footer className="mt-20 text-center">
                    <div className="border-t border-zinc-800 pt-8">
                        {/* Footer Disclaimer */}
                        <div className="bg-zinc-800/30 border border-zinc-700 rounded-lg p-3 mb-6 max-w-2xl mx-auto">
                            <div className="flex items-center justify-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-zinc-400" />
                                <p className="text-zinc-400 text-xs">
                                    Notice: Smart Contracts for Kitsu Locker have not been audited yet.
                                </p>
                            </div>
                        </div>

                        <p className="text-zinc-500 text-sm">
                            © 2024 KITSU TokenLocker. Built with mystical precision by the nine-tailed fox.
                        </p>
                        <p className="text-zinc-600 text-xs mt-2">
                            九尾の狐 | Nine-Tailed Prophecy 🦊✨
                        </p>
                    </div>
                </footer>
            </main>

            {/* Floating Particles Effect */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                {[...Array(20)].map((_, i) => (
                    <div
                        key={i}
                        className="absolute w-1 h-1 bg-purple-400/30 rounded-full animate-float"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                            animationDelay: `${Math.random() * 3}s`,
                            animationDuration: `${3 + Math.random() * 2}s`
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

export default App;
